import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Filter, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { API_BASE_URL } from '../config';

// Symbols loaded dynamically from API based on active + user permissions

const zoneColor = (si) => {
  if (si == null) return { bg: 'bg-dark-700', text: 'text-dark-400', label: '-' };
  if (si >= 71) return { bg: 'bg-green-900/40', text: 'text-green-400', label: 'GREEN' };
  if (si >= 31) return { bg: 'bg-dark-600', text: 'text-dark-300', label: 'GREY' };
  return { bg: 'bg-red-900/40', text: 'text-red-400', label: 'RED' };
};

const formatIST = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit',
    hour12: true,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const SignalHistoryPanel = () => {
  const [data, setData] = useState([]);
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [flipDetail, setFlipDetail] = useState(null);

  // Filters
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [flipsOnly, setFlipsOnly] = useState(false);
  const [anomaliesOnly, setAnomaliesOnly] = useState(true);
  const [zoneFilter, setZoneFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (selectedSymbols.length > 0) params.set('symbol', selectedSymbols.join(','));
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (flipsOnly) params.set('flips_only', 'true');
      if (anomaliesOnly) params.set('anomalies_only', 'true');
      if (zoneFilter) params.set('zone', zoneFilter);

      const response = await fetch(`${API_BASE_URL}/analytics/signal-history/?${params}`, {
        headers: { 'Authorization': `Basic ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch signal history');
      const result = await response.json();
      setData(result.results || []);
      if (result.available_symbols) {
        setAvailableSymbols(result.available_symbols);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSymbols, dateFrom, dateTo, flipsOnly, anomaliesOnly, zoneFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSymbol = (sym) => {
    setSelectedSymbols(prev =>
      prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]
    );
  };

  // Fetch flip detail: get the previous computation for the same symbol + date
  const handleFlipClick = async (row) => {
    if (expandedRow === row.id) {
      setExpandedRow(null);
      setFlipDetail(null);
      return;
    }
    setExpandedRow(row.id);

    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        symbol: row.symbol,
        date_from: row.price_date,
        date_to: row.price_date,
      });
      const response = await fetch(`${API_BASE_URL}/analytics/signal-history/?${params}`, {
        headers: { 'Authorization': `Basic ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        // All computations for same symbol + date, sorted by computed_at desc
        const rows = (result.results || []).sort((a, b) =>
          new Date(b.computed_at) - new Date(a.computed_at)
        );
        setFlipDetail(rows);
      }
    } catch (err) {
      console.error('Error fetching flip detail:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="dashboard-card px-4 py-3 space-y-2.5">
        {/* Row 1: Symbol chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          {availableSymbols.map(sym => (
            <button
              key={sym}
              onClick={() => toggleSymbol(sym)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                selectedSymbols.includes(sym)
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'bg-dark-700/80 text-dark-400 hover:text-white hover:bg-dark-600'
              }`}
            >
              {sym}
            </button>
          ))}
          {selectedSymbols.length > 0 && (
            <button
              onClick={() => setSelectedSymbols([])}
              className="px-2 py-1 text-[11px] text-dark-400 hover:text-white underline underline-offset-2"
            >
              clear
            </button>
          )}
        </div>

        {/* Row 2: Date, zone, toggles, refresh */}
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-dark-800 border border-dark-600 text-white text-[11px] rounded-md px-2 py-1 focus:outline-none focus:border-primary-500 w-[120px]"
          />
          <span className="text-dark-600 text-[11px]">–</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-dark-800 border border-dark-600 text-white text-[11px] rounded-md px-2 py-1 focus:outline-none focus:border-primary-500 w-[120px]"
          />

          <div className="h-5 w-px bg-dark-700" />

          <select
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            className="bg-dark-800 border border-dark-600 text-white text-[11px] rounded-md px-2 py-1 focus:outline-none focus:border-primary-500"
          >
            <option value="">All Zones</option>
            <option value="GREEN">GREEN</option>
            <option value="GREY">GREY</option>
            <option value="RED">RED</option>
          </select>

          <div className="h-5 w-px bg-dark-700" />

          {/* Flips toggle */}
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <button
              role="switch"
              aria-checked={flipsOnly}
              onClick={() => setFlipsOnly(!flipsOnly)}
              className={`relative w-7 h-4 rounded-full transition-colors ${flipsOnly ? 'bg-blue-500' : 'bg-dark-600'}`}
            >
              <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${flipsOnly ? 'left-[14px]' : 'left-[2px]'}`} />
            </button>
            <span className="text-[11px] text-dark-300">Flips</span>
          </label>

          {/* Anomalies toggle */}
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <button
              role="switch"
              aria-checked={anomaliesOnly}
              onClick={() => setAnomaliesOnly(!anomaliesOnly)}
              className={`relative w-7 h-4 rounded-full transition-colors ${anomaliesOnly ? 'bg-amber-500' : 'bg-dark-600'}`}
            >
              <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${anomaliesOnly ? 'left-[14px]' : 'left-[2px]'}`} />
            </button>
            <span className="text-[11px] text-dark-300">Anomalies</span>
          </label>

          {/* Refresh */}
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1 px-2.5 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-[11px] font-semibold transition-colors ml-auto"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Results count */}
      <div className="text-xs text-dark-500 flex items-center gap-3">
        <span>{data.length} row{data.length !== 1 ? 's' : ''} returned</span>
        {data.filter(r => r.is_flip).length > 0 && (
          <span className="text-blue-400 font-semibold">
            {data.filter(r => r.is_flip).length} flip{data.filter(r => r.is_flip).length !== 1 ? 's' : ''}
          </span>
        )}
        {data.filter(r => r.is_anomaly).length > 0 && (
          <span className="text-amber-400 font-semibold">
            {data.filter(r => r.is_anomaly).length} anomal{data.filter(r => r.is_anomaly).length !== 1 ? 'ies' : 'y'}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="dashboard-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b-2 border-dark-600 text-dark-400 uppercase tracking-wider">
              <th className="text-left py-2.5 px-3 font-semibold">Symbol</th>
              <th className="text-left py-2.5 px-3 font-semibold">Trading Date</th>
              <th className="text-left py-2.5 px-3 font-semibold">Computed At (IST)</th>
              <th className="text-right py-2.5 px-3 font-semibold">Close</th>
              <th className="text-center py-2.5 px-3 font-semibold">SI</th>
              <th className="text-center py-2.5 px-3 font-semibold">WRSI</th>
              <th className="text-center py-2.5 px-3 font-semibold">DRSI</th>
              <th className="text-left py-2.5 px-3 font-semibold">Signal Reason</th>
              <th className="text-center py-2.5 px-3 font-semibold">Source</th>
              <th className="text-center py-2.5 px-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const zone = zoneColor(row.smart_index_st);
              const isExpanded = expandedRow === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={`border-b border-dark-700/50 hover:bg-dark-700/30 transition-colors ${
                      row.is_anomaly ? 'border-l-2 border-l-amber-500 bg-amber-900/10' :
                      row.is_flip ? 'border-l-2 border-l-blue-500 bg-blue-900/10' : ''
                    }`}
                  >
                    <td className="py-2 px-3">
                      <span className="px-2 py-0.5 rounded bg-dark-600 text-white font-medium">{row.symbol}</span>
                    </td>
                    <td className="py-2 px-3 text-dark-200">{formatDate(row.price_date)}</td>
                    <td className="py-2 px-3 text-dark-300 tabular-nums">{formatIST(row.computed_at)}</td>
                    <td className="py-2 px-3 text-right text-dark-200 tabular-nums">
                      {row.close != null ? row.close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${zone.bg} ${zone.text}`}>
                        {row.smart_index_st ?? '-'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center text-dark-300 tabular-nums">{row.wrsi ?? '-'}</td>
                    <td className="py-2 px-3 text-center text-dark-300 tabular-nums">{row.drsi ?? '-'}</td>
                    <td className="py-2 px-3 text-dark-300 min-w-[300px] whitespace-normal leading-snug">
                      {row.signal_reason || '-'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className="px-1.5 py-0.5 rounded bg-dark-600 text-dark-400 text-[10px]">{row.source}</span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {row.is_flip && (
                          <button
                            onClick={() => handleFlipClick(row)}
                            className="px-2 py-0.5 rounded bg-blue-600 text-white text-[10px] font-bold hover:bg-blue-500 transition-colors"
                            title="Signal zone changed between cron runs"
                          >
                            FLIP {isExpanded ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />}
                          </button>
                        )}
                        {row.is_anomaly && (
                          <span
                            className="px-2 py-0.5 rounded bg-amber-600 text-white text-[10px] font-bold"
                            title="Signal may have changed for a wrong reason — investigate"
                          >
                            ANOMALY
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Flip detail expanded */}
                  {isExpanded && flipDetail && (
                    <tr className="bg-dark-800/80">
                      <td colSpan={10} className="p-4">
                        <div className="text-xs text-dark-300 mb-2 font-semibold flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          All computations for {row.symbol} on {formatDate(row.price_date)}
                        </div>
                        <div className="space-y-1">
                          {flipDetail.map((d, i) => {
                            const z = zoneColor(d.smart_index_st);
                            const isCurrent = d.id === row.id;
                            return (
                              <div
                                key={d.id}
                                className={`flex items-center gap-4 px-3 py-1.5 rounded ${
                                  isCurrent ? 'bg-red-900/20 border border-red-800/40' : 'bg-dark-700/50'
                                }`}
                              >
                                <span className="text-dark-500 w-16">{isCurrent ? 'CURRENT' : i === flipDetail.length - 1 ? 'FIRST' : `RUN ${flipDetail.length - i}`}</span>
                                <span className={`px-2 py-0.5 rounded font-bold ${z.bg} ${z.text}`}>
                                  SI={d.smart_index_st ?? '-'} {z.label}
                                </span>
                                <span className="text-dark-400">WRSI={d.wrsi ?? '-'}</span>
                                <span className="text-dark-400">DRSI={d.drsi ?? '-'}</span>
                                <span className="text-dark-400">Close={d.close != null ? d.close.toLocaleString() : '-'}</span>
                                <span className="text-dark-500 tabular-nums">@ {formatIST(d.computed_at)}</span>
                                {d.is_flip && <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold">FLIP</span>}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {data.length === 0 && !loading && (
              <tr>
                <td colSpan={10} className="py-12 text-center text-dark-500">
                  No signal history found. Adjust filters or wait for cron runs to populate data.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignalHistoryPanel;
